import type {
  ClassDef,
  Combat,
  ItemStats,
  JewelryTable,
  StatKey,
  UpgradeTable,
  UpgradeTables,
} from "./schema";

export type StatBlock = Record<StatKey, number>;

export interface DerivedStats {
  maxHp: number;
  maxMp: number;
  /** Attack power before buffs. */
  ap: number;
  /** Defense before buffs (gear only, 0 until items arrive). */
  def: number;
  /** 0..1 */
  critChance: number;
}

/**
 * PRD base-stat formulas. Shared by the server (authoritative) and the character screen
 * (preview), so both always show the same numbers.
 * - HP = hpBase + hpPerVit * VIT, plus the class bonus
 * - MP = mpBase + mpPerInt * INT
 * - AP = weapon base + primary stat * apPerPrimary
 * - Crit = DEX * critPerDex, capped
 */
/** How far along the level curve a level is: 0 up to `from`, 1 at the cap (combat.levelGrowth). */
export function levelShare(level: number, cfg: Combat): number {
  const g = cfg.levelGrowth;
  const x = Math.min(1, Math.max(0, (level - g.from) / Math.max(1, g.cap - g.from)));
  return x ** g.power;
}

/** A monster's HP and attack power grow with its level like a character's do. */
export function mobLevelMult(level: number, cfg: Combat): number {
  return 1 + levelShare(level, cfg) * cfg.levelGrowth.mobBonus;
}

export function deriveStats(
  cls: ClassDef,
  base: StatBlock,
  cfg: Combat,
  gear: GearBonus = NO_GEAR,
  level = 1,
): DerivedStats {
  const share = levelShare(level, cfg);
  const s: StatBlock = {
    str: base.str + (gear.str ?? 0),
    dex: base.dex + (gear.dex ?? 0),
    int: base.int + (gear.int ?? 0),
    vit: base.vit + (gear.vit ?? 0),
  };
  // An equipped weapon replaces the class's training weapon.
  const weaponAp = gear.weaponAp ?? cls.weapon.apBase;
  return {
    maxHp:
      Math.round((cfg.hpBase + cfg.hpPerVit * s.vit) * (1 + cls.hpBonusPct / 100)) +
      (gear.hp ?? 0) +
      Math.round(share * cls.levelHp),
    maxMp:
      Math.round(cfg.mpBase + cfg.mpPerInt * s.int) +
      (gear.mp ?? 0) +
      Math.round(share * cls.levelMp),
    ap:
      weaponAp +
      (gear.ap ?? 0) +
      s[cls.primary] * cfg.apPerPrimary +
      Math.round(share * cls.levelAp),
    def: gear.def ?? 0,
    critChance: Math.min(cfg.critCap, s.dex * cfg.critPerDex),
  };
}

/** Summed bonuses of everything equipped. `weaponAp` is the weapon's own attack power. */
export type GearBonus = ItemStats & { weaponAp?: number };
const NO_GEAR: GearBonus = {};

export interface EquippedItem {
  template: { slot: string; stats: ItemStats };
  rolls: { stat: keyof ItemStats; value: number }[];
  /** Upgrade level (+N). Raises the template's base stats by the upgrade table's gains. */
  plus?: number;
}

/** Slots that use the jewelry ladder; everything else equippable uses the gear ladder. */
export const JEWELRY_SLOTS: ReadonlySet<string> = new Set([
  "ring",
  "neck",
  "earring",
  "belt",
  "cape",
]);
/** Slots that cannot be upgraded at all (store cosmetics). */
export const NO_UPGRADE_SLOTS: ReadonlySet<string> = new Set([
  "glow",
  "wings",
  "mantle",
  "reward",
  "none",
]);

/** The upgrade ladder an item of this slot climbs, or undefined if it cannot be upgraded. */
export function upgradeTableFor(
  tables: UpgradeTables,
  slot: string,
): UpgradeTable | JewelryTable | undefined {
  if (NO_UPGRADE_SLOTS.has(slot)) return undefined;
  return JEWELRY_SLOTS.has(slot) ? tables.jewelry : tables.gear;
}

/** Cumulative base-stat multiplier for a +N item (PRD +upgrade table: +5%, +5%, +6%, ...). */
export function plusMultiplier(
  plus: number,
  table: { steps: { from: number; statGainPct: number }[] },
): number {
  let pct = 0;
  for (const step of table.steps) if (step.from < plus) pct += step.statGainPct;
  return 1 + pct / 100;
}

export function gearBonus(items: EquippedItem[], tables?: UpgradeTables): GearBonus {
  const out: GearBonus = {};
  for (const it of items) {
    const table = tables && upgradeTableFor(tables, it.template.slot);
    const mult = it.plus && table ? plusMultiplier(it.plus, table) : 1;
    for (const [k, raw] of Object.entries(it.template.stats) as [keyof ItemStats, number][]) {
      // Class guards and elemental resistances are flat percentages: a + level does not grow them.
      const v = k.startsWith("res") ? raw : Math.round(raw * mult);
      if (k === "ap" && it.template.slot === "weapon") out.weaponAp = (out.weaponAp ?? 0) + v;
      else out[k] = (out[k] ?? 0) + v;
    }
    for (const r of it.rolls) out[r.stat] = (out[r.stat] ?? 0) + r.value;
  }
  return out;
}
